/**
 * Checkout Errors Module
 *
 * Comprehensive error handling for checkout operations.
 *
 * Usage:
 *
 * ```typescript
 * import {
 *   handleCheckoutError,
 *   parseSaleorErrors,
 *   parseOrderAppErrors,
 *   getFieldErrors,
 *   isPaymentError,
 *   isRetryableError,
 * } from '@/lib/checkout/errors';
 *
 * // Parse API errors
 * const errors = parseSaleorErrors(response.errors);
 *
 * // Handle and enrich errors
 * const handled = handleCheckoutErrors(errors, {
 *   locale: 'en',
 *   operation: 'checkoutCreate',
 * });
 *
 * // Get field-specific errors for form display
 * const fieldErrors = getFieldErrors(handled);
 * // { email: 'Please enter a valid email address', ... }
 *
 * // Check error types
 * if (isPaymentError(handled[0])) {
 *   // Handle payment-specific error
 * }
 *
 * // Check if retry is possible
 * if (hasRetryableErrors(handled)) {
 *   const { delayMs, maxRetries } = getRetryConfig(handled);
 *   // Implement retry logic
 * }
 * ```
 */

// Types
// Error codes
export {
  ADYEN_ERROR_CODES,
  ALL_ERROR_CODES,
  getErrorMeta,
  isKnownErrorCode,
  NETWORK_ERROR_CODES,
  ORDER_APP_ERROR_CODES,
  SALEOR_ERROR_CODES,
  VALIDATION_ERROR_CODES,
} from './codes';
export type { ErrorCodeMeta } from './codes';
// Error handler
export {
  createCheckoutError,
  errorResult,
  getFieldErrors,
  getFirstErrorMessage,
  getRetryConfig,
  handleCheckoutError,
  handleCheckoutErrors,
  hasRetryableErrors,
  parseNetworkError,
  parseOrderAppErrors,
  parseSaleorErrors,
  RETRY_CONFIG,
  sleep,
  withRetry,
} from './handler';
export type { ErrorHandlerOptions } from './handler';
// Error messages
export { ERROR_MESSAGES, getErrorMessage, getErrorMessagesForLocale } from './messages';
export { localizeApiErrors } from './localizeApiErrors';
export {
  handleOrderCreateErrors,
  hasOrderCreateShoppingBagError,
  isOrderCreateShoppingBagErrorCode,
  ORDER_CREATE_SHOPPING_BAG_ERROR_CODES,
} from './handleOrderCreateErrors';
export type {
  HandleOrderCreateErrorsParams,
  OrderCreateErrorInput,
  OrderCreateShoppingBagErrorCode,
} from './handleOrderCreateErrors';
export {
  parseShoppingBagLineErrors,
  splitOrderAppErrorsForDisplay,
} from './parseShoppingBagLineErrors';
export type { ShoppingBagLineErrors } from './parseShoppingBagLineErrors';
export { useCheckoutShoppingBagLineErrors } from './useCheckoutShoppingBagLineErrors';
export type { ErrorMessageKey } from './messages';
// Type guards
export {
  isAddressError,
  isErrorResult,
  isExpiredError,
  isNetworkError,
  isPaymentError,
  isRecoverableError,
  isRetryableError,
  isStockError,
  isSuccessResult,
  isValidationError,
} from './types';
export type {
  AddressError,
  AuthError,
  BaseCheckoutError,
  CheckoutError,
  CheckoutErrorResult,
  CheckoutResult,
  CheckoutSuccessResult,
  ErrorCategory,
  ErrorSeverity,
  ExpiredError,
  NetworkError,
  PaymentError,
  RateLimitError,
  ServerError,
  StockError,
  UnknownError,
  ValidationError,
} from './types';
